import React, { useState } from "react";
import SheetMaterialLookupPanel from "./SheetMaterialLookupPanel";
import BoqHistoryPanel from "./BoqHistoryPanel";
import BoqCommentsPanel from "./BoqCommentsPanel";
import Modal from "react-modal";
import {
  writeBoqTable,
  readBoqTable,
  readChildSheetTotal,
  findChildSheetSummaryRow,
  computeBoqHierarchy,
  insertBoqRow,
  readBoqSummary,
  getSelectedBoqSheetRow,
} from "../excel/boq-table";
import { writeCustomBoqTable, readCustomBoqTable } from "../excel/custom-boq-table";
import { getSafeSheetName } from "../excel/sheetUtils";
import { runExcelWithToast } from "../excel/runExcelWithToast";
import {
  fetchProductTableRows,
  replaceProductTableRows,
  fetchChildTableTotals,
  ensureProjectBoqRoot,
  createOrGetChildBoqTable,
  fetchBoqVersions,
  fetchApprovals,
  revokeApproval,
  fetchComments,
  sendPresence,
  createBoqVersion,
  updateBoqVersion,
  deleteBoqVersion,
  createBoqSnapshot,
  fetchBoqSnapshots,
} from "../api/productTablesApi";
import { confirmDialog } from "../utils/confirmDialog";
import { colorFor } from "../utils/presenceColors";
import {
  startSelectionWatch, stopSelectionWatch, syncPresenceMarks, clearPresenceMarks,
} from "../excel/presenceMarks";

const formatSnapshotDate = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString(undefined, {
      year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return String(iso);
  }
};

export default function BOQManager({
  onBack,
  project,
  token,
  autoRender = false,
  onAutoRenderDone,
  parentTableId,
  customers = [],
  canEdit = true,
  canManageVersions = false,
  userRole,
}) {
  const [showLookupModal, setShowLookupModal] = useState(false);
  const [activeChildSheet, setActiveChildSheet] = useState(null);
  const [busy, setBusy] = useState(false);
  const [statusMessage, setStatusMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [formData, setFormData] = useState({
    date: new Date().toISOString().split("T")[0],
    customer: "",
    description: "",
    quantity: "",
  });
  const [boqRows, setBoqRows] = useState([]);
  const [childTotals, setChildTotals] = useState({});
  const [rootTableId, setRootTableId] = useState(parentTableId || null);
  const [baseSheetName, setBaseSheetName] = useState("I");
  const [rootSheetName, setRootSheetName] = useState("I0");
  const [showHistory, setShowHistory] = useState(false);

  // Version management
  const [versions, setVersions] = useState([]);
  const [selectedVersionId, setSelectedVersionId] = useState(parentTableId || null);
  const [newVersionName, setNewVersionName] = useState("");
  const [showLoadConfirm, setShowLoadConfirm] = useState(false);
  const autoRenderStartedRef = React.useRef(false);

  // Sync status: last time this version was saved to the server, and whether
  // the currently open TOTAL sheet still matches what the server has.
  const [lastSnapshot, setLastSnapshot] = useState(null);
  const [excelDirty, setExcelDirty] = useState(null); // null = unknown, true/false = checked
  const [checkingDirty, setCheckingDirty] = useState(false);
  const [newRowName, setNewRowName] = useState("");
  const [saveNote, setSaveNote] = useState("");
  const [approval, setApproval] = useState(null); // active approval (locked) of the selected version
  const [showComments, setShowComments] = useState(false);
  const [commentCount, setCommentCount] = useState(0);
  const [onlineOthers, setOnlineOthers] = useState([]);
  const [findText, setFindText] = useState("");
  const findCursorRef = React.useRef(0);
  // Someone else saved this project's BOQ after we last loaded/saved it.
  const [remoteChange, setRemoteChange] = useState(null);
  const seenSnapshotIdRef = React.useRef(null);

  // Resolve customer from project.customer_id to "name / inn"
  const customerDisplay = React.useMemo(() => {
    const cid = project?.customer_id;
    if (!cid) return "";
    const c = customers.find((x) => String(x.id) === String(cid));
    if (!c) return String(cid);
    const parts = [c.name];
    if (c.inn) parts.push(c.inn);
    return parts.join(" / ");
  }, [project, customers]);

  const selectedVersion = React.useMemo(
    () => versions.find((v) => String(v.id) === String(selectedVersionId)) || null,
    [versions, selectedVersionId]
  );

  // Build the payload passed into writeBoqTable — includes real project name,
  // customer "name / inn", fixed document number, and language.
  const buildExcelProjectData = React.useCallback(() => {
    const cid = project?.customer_id;
    const c = cid ? customers.find((x) => String(x.id) === String(cid)) : null;
    const customerText = c
      ? (c.inn ? `${c.name} / ${c.inn}` : c.name)
      : "";

    return {
      name: project?.name || "",
      description: project?.description || "",
      date: formData.date,
      customer: customerText,
      ref: project?.doc_number || "",
      author: project?.author || "",
      location: project?.location || "",
      quantity: formData.quantity || project?.area || "",
      area: project?.area,
      language: (project?.language || "geo").toLowerCase(),
      costConfig: project?.cost_config || null,
    };
  }, [project, customers, formData.date, formData.quantity]);

  const buildChildSheetName = React.useCallback(
    (row, idx) => {
      const sourceText = (
        row?.description ||
        row?.Material_Name_GEO ||
        row?.title ||
        ""
      )
        .toString()
        .trim();

      const stopWords = new Set(["და", "ან", "თუ", "რომ", "ზე", "ში", "თვის", "კი"]);
      const words = sourceText
        .split(/[\s\-_/.,;:()]+/u)
        .map((word) => word.trim())
        .filter((word) => word && !stopWords.has(word.toLowerCase()));

      const initials = words
        .map((word) => (Array.from(word)[0] || ""))
        .join("")
        .toLocaleUpperCase("ka-GE")
        .slice(0, 12);

      const baseName = initials || "ჩაილდ";
      // Named by the row number so inserting sub-rows never renames other sheets
      // ("1" -> "1.0_..." as before, "2.1" -> "2.1_...").
      const num = String(row?.number ?? row?.Display_ID ?? "").trim().replace(/\.0+$/, "") || String(idx + 1);
      const prefix = num.includes(".") ? num : `${num}.0`;
      return getSafeSheetName(`${prefix}_${baseName}-დეტალური`);
    },
    []
  );

  const worksheetExists = React.useCallback(async (sheetTitle) => {
    return Excel.run(async (context) => {
      const sheet = context.workbook.worksheets.getItemOrNullObject(sheetTitle);
      sheet.load("name");
      await context.sync();
      return !sheet.isNullObject;
    });
  }, []);

  const buildRootRecords = React.useCallback(
    (rowsFromExcel, knownRows) => {
      // Keep the numbers typed in the TOTAL sheet ("2", "2.1", ...); rows
      // without one become the next top-level chapter.
      let topCounter = 0;
      const numbers = rowsFromExcel.map((row) => {
        if (row.number) {
          if (/^\d+$/.test(row.number)) topCounter = Number(row.number);
          return row.number;
        }
        topCounter += 1;
        return String(topCounter);
      });
      return rowsFromExcel.map((row, idx) => ({
        Display_ID: numbers[idx],
        Material_Name_GEO: row.description || "",
        Base_Unit: row.unit || "",
        Unit_Rate: Number(row.totalPrice) || 0,
        Currency: "GEL",
        Discipline: "BOQ Root",
        // Match the saved row by number (rows can be inserted in the middle).
        Child_Table_Id:
          knownRows.find((k) => String(k.number || "").replace(/\.0+$/, "") === numbers[idx])
            ?.child_table_id || null,
      }));
    },
    []
  );

  const loadRootRowsAndTotals = React.useCallback(
    async (tableId) => {
      if (!tableId || !token) return { mappedRows: [], totalsMap: {} };

      const [rows, totals] = await Promise.all([
        fetchProductTableRows(token, tableId),
        fetchChildTableTotals(token, tableId),
      ]);

      const mappedRows = (rows || []).map((row) => ({
        description: row.Material_Name_GEO || "",
        quantity: row.Display_ID || "",
        number: row.Display_ID || "",
        unit: row.Base_Unit || "",
        pricePerUnit: row.Unit_Rate || 0,
        totalPrice: row.Unit_Rate || 0,
        materialCost: 0,
        labourCost: 0,
        equipmentCost: 0,
        percentage: "",
        child_table_id: row.Child_Table_Id || null,
        row_id: row.id,
      }));
      setBoqRows(mappedRows);

      const totalsMap = {};
      (totals || []).forEach((item) => {
        totalsMap[item.row_id] = {
          material: item.material || 0,
          labour: item.labour || 0,
          equipment: item.equipment || 0,
          total: item.total || 0,
        };
      });
      setChildTotals(totalsMap);

      return { mappedRows, totalsMap };
    },
    [token]
  );

  // Fetch the most recent server save (snapshot) for this BOQ version, so we
  // can show "Last saved ... by ..." without opening the history panel.
  const loadLastSnapshot = React.useCallback(async () => {
    if (!project?.id || !token) {
      setLastSnapshot(null);
      return;
    }
    try {
      const res = await fetchBoqSnapshots(token, project.id, 20, 0);
      const items = res?.items || [];
      const forVersion = items.find((s) => String(s.version_id || "") === String(rootTableId || ""));
      setLastSnapshot(forVersion || items[0] || null);
    } catch (err) {
      console.warn("Failed to load BOQ snapshot history:", err);
      setLastSnapshot(null);
    }
  }, [project, token, rootTableId]);

  // ── Live "someone else changed this project" notice ──
  // Every Save writes a snapshot with its author, so we poll the newest one.
  // Our own saves/loads move the baseline (seenSnapshotIdRef); a newer snapshot
  // by another user raises the banner.
  const myUserId = React.useMemo(() => {
    try {
      const b64 = String(token || "").split(".")[1].replace(/-/g, "+").replace(/_/g, "/");
      return Number(JSON.parse(atob(b64)).sub) || null;
    } catch {
      return null;
    }
  }, [token]);

  const fetchNewestSnapshot = React.useCallback(async () => {
    const res = await fetchBoqSnapshots(token, project.id, 1, 0);
    return (res?.items || [])[0] || null;
  }, [token, project]);

  // Baseline = "what I have on screen"; call after our own Load / Save.
  const acknowledgeLatestSnapshot = React.useCallback(async () => {
    setRemoteChange(null);
    if (!project?.id || !token) return;
    try {
      const latest = await fetchNewestSnapshot();
      seenSnapshotIdRef.current = latest ? latest.id : null;
    } catch {
      /* keep the old baseline */
    }
  }, [project, token, fetchNewestSnapshot]);

  React.useEffect(() => {
    seenSnapshotIdRef.current = null;
    setRemoteChange(null);
    if (!project?.id || !token) return undefined;

    let cancelled = false;
    const check = async () => {
      if (document.hidden) return;
      try {
        const latest = await fetchNewestSnapshot();
        if (cancelled || !latest) return;
        if (seenSnapshotIdRef.current === null) {
          seenSnapshotIdRef.current = latest.id; // first look = baseline
          return;
        }
        if (latest.id === seenSnapshotIdRef.current) return;
        if (myUserId && latest.created_by_id === myUserId) {
          seenSnapshotIdRef.current = latest.id; // my own save (e.g. other window)
          return;
        }
        setRemoteChange({
          id: latest.id,
          by: latest.created_by_name || "Someone",
          at: latest.created_at,
          version: latest.version_name || "",
        });
      } catch {
        /* offline / transient — try again next tick */
      }
    };
    check();
    const timer = setInterval(check, 20000);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [project?.id, token, myUserId, fetchNewestSnapshot]);

  // Remember the open version for the Tools page, and load its lock state + open comments.
  const refreshApproval = React.useCallback(async () => {
    if (!project?.id || !token || !rootTableId) { setApproval(null); return; }
    try {
      const list = await fetchApprovals(token, project.id);
      setApproval((list || []).find((a) => String(a.version_id) === String(rootTableId)) || null);
    } catch {
      setApproval(null);
    }
  }, [project?.id, token, rootTableId]);

  React.useEffect(() => {
    if (rootTableId) {
      try {
        window.localStorage.setItem("boq.activeVersionId", String(rootTableId));
        window.localStorage.setItem("boq.activeRootSheet", rootSheetName || `${baseSheetName}0`);
      } catch { /* ignore */ }
    }
    refreshApproval();
    if (project?.id && token && rootTableId) {
      fetchComments(token, project.id, rootTableId)
        .then((list) => setCommentCount((list || []).filter((c) => !c.resolved).length))
        .catch(() => setCommentCount(0));
    } else {
      setCommentCount(0);
    }
  }, [rootTableId, rootSheetName, baseSheetName, project?.id, token, refreshApproval]);

  // Presence: tell the server we are here (every 30 s, and soon after the selected BOQ row
  // changes) and show who else is, with the row they are on.
  const focusRef = React.useRef(null);
  const isBoqSheetRef = React.useRef(() => false);
  isBoqSheetRef.current = (name) => name === (rootSheetName || `${baseSheetName}0`) || String(name).includes("დეტალური");

  React.useEffect(() => {
    if (!project?.id || !token) return undefined;
    let cancelled = false;
    let debounce = null;
    const beat = async () => {
      if (document.hidden) return;
      try {
        const others = await sendPresence(token, project.id, focusRef.current);
        if (!cancelled) setOnlineOthers(Array.isArray(others) ? others : []);
      } catch { /* transient */ }
    };
    beat();
    const timer = setInterval(beat, 30000);
    startSelectionWatch((focus) => {
      const prev = focusRef.current;
      focusRef.current = focus;
      if (cancelled || (prev?.sheet === focus?.sheet && prev?.label === focus?.label)) return;
      clearTimeout(debounce);
      debounce = setTimeout(beat, 1500);
    }, (name) => isBoqSheetRef.current(name)).catch(() => undefined);
    return () => {
      cancelled = true;
      clearInterval(timer);
      clearTimeout(debounce);
      stopSelectionWatch();
      clearPresenceMarks();
      focusRef.current = null;
      setOnlineOthers([]);
    };
  }, [project?.id, token]);

  // Tint the rows colleagues have selected (restored when they move on or this tab closes).
  React.useEffect(() => {
    syncPresenceMarks(onlineOthers);
  }, [onlineOthers]);

  // Whenever the selected version changes, the sync status becomes unknown
  // until either an action runs or the user checks manually (the open sheet
  // may still be showing a different, previously loaded version).
  React.useEffect(() => {
    setExcelDirty(null);
    loadLastSnapshot();
  }, [rootTableId, loadLastSnapshot]);

  // Compare the currently open TOTAL sheet's grand total against the totals
  // we last fetched from the server for this version.
  const checkExcelSyncStatus = React.useCallback(async () => {
    if (!rootTableId) {
      setExcelDirty(null);
      return;
    }
    setCheckingDirty(true);
    try {
      const sheetName = rootSheetName || `${baseSheetName}0`;
      const exists = await worksheetExists(sheetName);
      if (!exists) {
        setExcelDirty(null);
        return;
      }

      const rowsFromExcel = await readBoqTable(sheetName);
      const excelTotal = rowsFromExcel.reduce((sum, r) => sum + (Number(r.totalPrice) || 0), 0);

      const serverTotal = boqRows.reduce((sum, row) => {
        const ct = row.row_id ? childTotals[row.row_id] : null;
        const total = ct?.total ?? row.totalPrice ?? 0;
        return sum + (Number(total) || 0);
      }, 0);

      setExcelDirty(Math.abs(excelTotal - serverTotal) > 0.5);
    } catch (err) {
      console.warn("Unable to check Excel vs server sync status:", err);
      setExcelDirty(null);
    } finally {
      setCheckingDirty(false);
    }
  }, [rootTableId, rootSheetName, baseSheetName, boqRows, childTotals, worksheetExists]);

  // Map server-side child ProductTableRow records into the shape writeCustomBoqTable expects.
  // Tolerates both "LABOR" and "LABOUR" spellings (legacy BOQ vs resource-catalog-v2).
  const mapServerChildRows = React.useCallback((persistedChildRows) => {
    return (persistedChildRows || []).map((childRow, childIdx) => {
      const rt = String(childRow.Resource_Type || childRow.Discipline || "").toUpperCase();
      const isMat = rt === "MATERIAL";
      const isLab = rt === "LABOR" || rt === "LABOUR";
      const isEq = rt === "EQUIPMENT";
      const rate = Number(childRow.Unit_Rate) || 0;
      const qty = Number(childRow.Qty_Per_Work_Unit) || 1;
      return {
        N: childIdx + 1,
        display_id: childRow.Display_ID || String(childIdx + 1),
        title: childRow.Material_Name_GEO || "",
        dimension: childRow.Base_Unit || "",
        norm_qty: childRow.Qty_Per_Work_Unit || 0,
        resource_type: isLab ? "LABOR" : rt,
        work_code: childRow.Work_Code || "",
        mat_price: isMat ? rate : 0,
        mat_total: isMat ? rate * qty : 0,
        salary_price: isLab ? rate : 0,
        salary_total: isLab ? rate * qty : 0,
        transport_price: isEq ? rate : 0,
        transport_total: isEq ? rate * qty : 0,
      };
    });
  }, []);

  // Render every chapter's child detail sheet straight from the server so the
  // TOTAL sheet can reference real worksheets instead of only static numbers.
  // Returns { childTotalsForExcel, childSheetRefs } keyed by root row index.
  const renderChildSheetsFromServer = React.useCallback(
    async (mappedRows) => {
      const childTotalsForExcel = {};
      const childSheetRefs = {};
      const projectDataForChild = {
        language: project?.language || "geo",
        costConfig: project?.cost_config || null,
      };

      // A parent (e.g. "2" with 2.1, 2.2) only sums its children; the child
      // rows own the detail sheets.
      const hierarchy = computeBoqHierarchy(mappedRows.map((r) => r.number));

      for (let idx = 0; idx < mappedRows.length; idx += 1) {
        const row = mappedRows[idx];
        if (!(row.description || "").trim()) continue;
        if (hierarchy[idx].lastDescendant > idx) continue;

        // Prefer the link stored on the row; otherwise ask the server to
        // resolve it from the saved child bindings (or create a fresh child).
        let childTableId = row.child_table_id;
        if (!childTableId && row.row_id) {
          try {
            const childTable = await createOrGetChildBoqTable(
              token,
              rootTableId,
              row.row_id,
              baseSheetName
            );
            childTableId = childTable?.id || null;
          } catch (err) {
            console.warn(`Failed to resolve child table for row ${row.row_id}:`, err);
          }
        }
        if (!childTableId) continue;

        const childSheetTitle = buildChildSheetName(row, idx);

        let persistedChildRows = [];
        try {
          persistedChildRows = await fetchProductTableRows(token, childTableId);
        } catch (err) {
          console.warn(`Failed to fetch child rows for ${childSheetTitle}:`, err);
          continue;
        }

        try {
          await writeCustomBoqTable(
            projectDataForChild,
            mapServerChildRows(persistedChildRows),
            childSheetTitle,
            1,
            row.description || ""
          );
        } catch (err) {
          console.warn(`Failed to write child sheet ${childSheetTitle}:`, err);
          continue;
        }

        try {
          const totals = await readChildSheetTotal(childSheetTitle);
          childTotalsForExcel[idx] = totals;
          const summaryRow = await findChildSheetSummaryRow(childSheetTitle);
          if (summaryRow > 0) {
            childSheetRefs[idx] = { sheetName: childSheetTitle, summaryRow };
          }
        } catch (err) {
          console.warn(`Failed to read child totals for ${childSheetTitle}:`, err);
        }
      }

      return { childTotalsForExcel, childSheetRefs };
    },
    [token, project, rootTableId, baseSheetName, buildChildSheetName, mapServerChildRows]
  );

  const applyRoot = React.useCallback(
    async (root) => {
      const projectDefaultBase = project?.id ? `P${project.id}` : "BOQ";
      setRootTableId(root.id);
      setBaseSheetName(root.base_sheet_name || projectDefaultBase);
      setRootSheetName(
        root.display_name ||
          `${root.base_sheet_name || projectDefaultBase}${root.sheet_level || 0}`
      );
      await loadRootRowsAndTotals(root.id);
    },
    [project, loadRootRowsAndTotals]
  );

  const loadVersionsAndSelect = React.useCallback(
    async (preferId) => {
      if (!project?.id || !token) return;
      const list = await fetchBoqVersions(token, project.id);
      setVersions(list || []);
      const primary = (list || []).find((v) => v.is_primary_version);
      const chosen =
        (preferId && (list || []).find((v) => String(v.id) === String(preferId))) ||
        primary ||
        (list || [])[0] ||
        null;
      if (chosen) {
        setSelectedVersionId(chosen.id);
        await applyRoot(chosen);
      }
      return { list, chosen };
    },
    [project, token, applyRoot]
  );

  React.useEffect(() => {
    if (project) {
      setFormData((prev) => ({
        ...prev,
        date: project.date ? project.date.split("T")[0] : prev.date,
        customer: project.customer_id || prev.customer,
        description: project.description || prev.description,
        quantity: project.area ? String(project.area) : prev.quantity,
      }));
    }

    if (!project?.id || !token) {
      setBoqRows([]);
      return;
    }

    const initRoot = async () => {
      try {
        setBusy(true);
        // Ensure at least one root (primary) exists, then refresh versions list.
        if (!parentTableId) {
          await ensureProjectBoqRoot(token, project.id, `P${project.id}`);
        }
        await loadVersionsAndSelect(parentTableId);
      } catch (error) {
        console.error("Failed to initialize BOQ root:", error);
        setErrorMessage(error?.message || "Unable to initialize BOQ root table.");
      } finally {
        setBusy(false);
      }
    };

    initRoot();
  }, [project, parentTableId, token, loadVersionsAndSelect]);

  // ═══ Version management handlers ═══

  const handleSwitchVersion = async (versionId) => {
    if (!versionId || versionId === selectedVersionId) return;
    const next = versions.find((v) => String(v.id) === String(versionId));
    if (!next) return;
    setBusy(true);
    setStatusMessage("");
    setErrorMessage("");
    try {
      setSelectedVersionId(next.id);
      await applyRoot(next);
    } catch (error) {
      setErrorMessage(error?.message || "Unable to switch version.");
    } finally {
      setBusy(false);
    }
  };

  const handleCreateVersion = async () => {
    const name = (newVersionName || "").trim();
    if (!name || !project?.id || !token) return;
    setBusy(true);
    setStatusMessage("");
    setErrorMessage("");
    try {
      const created = await createBoqVersion(token, project.id, {
        version_name: name,
        base_name: `P${project.id}`,
        set_as_primary: false,
      });
      setNewVersionName("");
      await loadVersionsAndSelect(created.id);
      setStatusMessage(`Version "${name}" created.`);
    } catch (error) {
      setErrorMessage(error?.message || "Unable to create version.");
    } finally {
      setBusy(false);
    }
  };

  const handleSetPrimary = async () => {
    if (!selectedVersionId || !project?.id || !token) return;
    setBusy(true);
    setStatusMessage("");
    setErrorMessage("");
    try {
      await updateBoqVersion(token, project.id, selectedVersionId, { set_as_primary: true });
      await loadVersionsAndSelect(selectedVersionId);
      setStatusMessage("Primary version updated.");
    } catch (error) {
      setErrorMessage(error?.message || "Unable to set primary version.");
    } finally {
      setBusy(false);
    }
  };

  const handleDeleteVersion = async () => {
    if (!selectedVersionId || !project?.id || !token) return;
    if (!(await confirmDialog("Delete this BOQ version? This cannot be undone."))) return;
    setBusy(true);
    setStatusMessage("");
    setErrorMessage("");
    try {
      await deleteBoqVersion(token, project.id, selectedVersionId);
      await loadVersionsAndSelect(null);
      setStatusMessage("Version deleted.");
    } catch (error) {
      setErrorMessage(error?.message || "Unable to delete version.");
    } finally {
      setBusy(false);
    }
  };

  // ═══ Delete all BOQ (root + child) sheets from the workbook ═══
  // Creates a __boq_tmp__ guard sheet ONLY if deletion would leave zero sheets.
  // Returns true if the guard was created (so the caller can clean it up later).

  const deleteAllBoqSheets = async () => {
    return Excel.run(async (context) => {
      const workbook = context.workbook;
      const sheets = workbook.worksheets;
      sheets.load("items/name");
      await context.sync();

      const isBoqSheet = (name) => {
        if (!name) return false;
        // Child sheets use the "X.Y_*-დეტალური" pattern
        if (/-დეტალური$/.test(name)) return true;
        // Root sheets of any known version
        for (const v of versions) {
          if (v.display_name && v.display_name === name) return true;
          if (v.base_sheet_name && v.base_sheet_name === name) return true;
        }
        if (name === rootSheetName) return true;
        if (name === `${baseSheetName}0`) return true;
        return false;
      };

      const allNames = sheets.items.map((s) => s.name);
      const toDelete = allNames.filter(isBoqSheet);
      const remaining = allNames.filter((n) => !toDelete.includes(n));

      // Only add a guard sheet when Excel would otherwise end up with zero sheets.
      let guardCreated = false;
      if (remaining.length === 0 && toDelete.length > 0) {
        const existingGuard = workbook.worksheets.getItemOrNullObject("__boq_tmp__");
        existingGuard.load("name");
        await context.sync();
        if (existingGuard.isNullObject) {
          workbook.worksheets.add("__boq_tmp__").activate();
          guardCreated = true;
        } else {
          existingGuard.activate();
        }
        await context.sync();
      }

      for (const s of sheets.items) {
        if (s.name === "__boq_tmp__") continue;
        if (isBoqSheet(s.name)) s.delete();
      }
      await context.sync();

      return guardCreated;
    });
  };

  // Remove the temporary guard sheet if it's no longer needed.
  const removeBoqTmpGuard = async () => {
    return Excel.run(async (context) => {
      const workbook = context.workbook;
      const sheets = workbook.worksheets;
      sheets.load("items/name");
      await context.sync();

      const guard = sheets.items.find((s) => s.name === "__boq_tmp__");
      if (!guard) return;

      // Never leave the workbook with zero sheets.
      const hasOther = sheets.items.some((s) => s.name !== "__boq_tmp__");
      if (!hasOther) return;

      // If the guard is currently active, activate another sheet first.
      const other = sheets.items.find((s) => s.name !== "__boq_tmp__");
      if (other) other.activate();
      await context.sync();

      guard.delete();
      await context.sync();
    });
  };

  const handleCreateBOQ = async () => {
    setBusy(true);
    setStatusMessage("");
    setErrorMessage("");

    try {
      const boqData = boqRows.map((row, idx) => {
        const ct = row.row_id && childTotals[row.row_id] ? childTotals[row.row_id] : null;
        return {
          ...row,
          materialCost: ct?.material || row.materialCost || 0,
          labourCost: ct?.labour || row.labourCost || 0,
          equipmentCost: ct?.equipment || row.equipmentCost || 0,
          totalPrice: ct?.total || row.totalPrice || 0,
        };
      });
      const childTotalsForExcel = {};
      boqData.forEach((row, idx) => {
        if (row.row_id && childTotals[row.row_id]) {
          childTotalsForExcel[idx] = childTotals[row.row_id];
        }
      });

      await runExcelWithToast({
        action: () =>
          writeBoqTable(
            buildExcelProjectData(),
            boqData,
            childTotalsForExcel,
            rootSheetName || `${baseSheetName}0`
          ),
        successMessage: "BOQ table created successfully",
      });

      setExcelDirty(false);
      setStatusMessage("BOQ table created successfully!");
    } catch (error) {
      console.error("Create BOQ failed", error);
      setErrorMessage(error?.message || "Unable to create BOQ table.");
    } finally {
      setBusy(false);
    }
  };

  React.useEffect(() => {
    autoRenderStartedRef.current = false;
  }, [project?.id, autoRender]);

  React.useEffect(() => {
    if (!autoRender || autoRenderStartedRef.current) return;
    if (!rootTableId || busy) return;

    autoRenderStartedRef.current = true;

    // The ribbon "ატვირთვა" button means "load this project's BOQ from the
    // database". Rendering from in-memory state (handleCreateBOQ) produced an
    // empty TOTAL sheet, so open the same confirmed Load (server -> Excel)
    // flow the Load button uses, which also rebuilds the child sheets.
    setShowLoadConfirm(true);
    if (typeof onAutoRenderDone === "function") onAutoRenderDone();
  }, [autoRender, rootTableId, busy, onAutoRenderDone]);

  const handleLoadBoqFromServer = async () => {
    if (!rootTableId || !token) return;
    // Show confirmation dialog — loading will wipe other BOQ sheets.
    setShowLoadConfirm(true);
  };

  const performLoadBoqFromServer = async () => {
    if (!rootTableId || !token) return;
    setShowLoadConfirm(false);
    setBusy(true);
    setStatusMessage("");
    setErrorMessage("");
    try {
      // 1. Clean up any existing BOQ sheets in the workbook first.
      try {
        await deleteAllBoqSheets();
      } catch (err) {
        console.warn("Failed to delete existing BOQ sheets:", err);
      }

      // 2. Load the selected version from the server and render.
      const { mappedRows, totalsMap } = await loadRootRowsAndTotals(rootTableId);

      // 2a. Rebuild each chapter's child detail sheet from the server. This is
      //     what makes "Load" pull the resources stored in the database — the
      //     TOTAL sheet then references these sheets via live formulas.
      const { childTotalsForExcel: childSheetTotals, childSheetRefs } =
        await renderChildSheetsFromServer(mappedRows);

      const childTotalsForExcel = {};
      const enrichedRows = mappedRows.map((row, idx) => {
        const serverCt = row.row_id && totalsMap[row.row_id] ? totalsMap[row.row_id] : null;
        // Prefer totals just read back from the freshly-written child sheet;
        // fall back to the server-computed breakdown.
        const ct = childSheetTotals[idx] || serverCt || null;
        if (ct) childTotalsForExcel[idx] = ct;
        return {
          ...row,
          materialCost: ct?.material || 0,
          labourCost: ct?.labour || 0,
          equipmentCost: ct?.equipment || 0,
          totalPrice: ct?.total || row.totalPrice || 0,
        };
      });

      await runExcelWithToast({
        action: () =>
          writeBoqTable(
            buildExcelProjectData(),
            enrichedRows,
            childTotalsForExcel,
            rootSheetName || `${baseSheetName}0`,
            childSheetRefs
          ),
        successMessage: "BOQ loaded from server",
      });

      // 3. The new BOQ sheet exists now, so the temp guard is no longer needed.
      try {
        await removeBoqTmpGuard();
      } catch (err) {
        console.warn("Failed to remove __boq_tmp__ guard:", err);
      }

      setExcelDirty(false);
      await acknowledgeLatestSnapshot();
      if (!mappedRows.length) {
        setStatusMessage(
          "BOQ loaded, but this version has no chapters saved on the server yet. " +
            "If you expected data here, check the BOQ Version selector — another version may hold it."
        );
      } else {
        const childCount = Object.keys(childSheetRefs).length;
        setStatusMessage(
          `BOQ loaded from server (${mappedRows.length} chapter(s), ${childCount} child sheet(s)).`
        );
      }
    } catch (error) {
      console.error("Load BOQ from server failed", error);
      setErrorMessage(error?.message || "Unable to load BOQ from server.");
    } finally {
      setBusy(false);
    }
  };

  const handleSaveBoqToServer = async () => {
    if (!rootTableId || !token) return;

    setBusy(true);
    setStatusMessage("");
    setErrorMessage("");
    try {
      const rowsFromExcel = await readBoqTable(rootSheetName || `${baseSheetName}0`);
      const records = buildRootRecords(rowsFromExcel, boqRows);

      // Safety net: Save replaces the server rows, so warn before rows disappear
      // or before overwriting someone else's newer save.
      try {
        const serverRows = await fetchProductTableRows(token, rootTableId);
        const keyOf = (v) => String(v || "").trim().replace(/\.0+$/, "");
        const nums = new Set(records.map((r) => keyOf(r.Display_ID)));
        const names = new Set(records.map((r) => String(r.Material_Name_GEO || "").trim().toLowerCase()));
        const removed = (serverRows || []).filter(
          (r) =>
            !nums.has(keyOf(r.Display_ID)) &&
            !names.has(String(r.Material_Name_GEO || "").trim().toLowerCase())
        );
        const parts = [];
        if (remoteChange) {
          parts.push(`${remoteChange.by} უფრო ახალი ცვლილებები შეინახა — შენი შენახვა მათ გადააწერს.`);
        }
        if (removed.length) {
          parts.push(
            `სერვერიდან წაიშლება ${removed.length} სტრიქონი: ` +
              removed.slice(0, 6).map((r) => `${r.Display_ID || ""} ${r.Material_Name_GEO || ""}`.trim()).join("; ") +
              (removed.length > 6 ? " …" : "")
          );
        }
        if (parts.length && !(await confirmDialog(`${parts.join("\n\n")}\n\nგაგრძელება?`))) {
          setBusy(false);
          return;
        }
      } catch (guardError) {
        console.warn("Save guard skipped:", guardError);
      }

      const savedRootRows = await replaceProductTableRows(token, rootTableId, records);

      // Persist each existing child worksheet into its bound child table
      for (let idx = 0; idx < (savedRootRows || []).length; idx += 1) {
        const rootRow = savedRootRows[idx];
        const childTableId = rootRow?.Child_Table_Id;
        if (!childTableId) continue;

        const childSheetTitle = buildChildSheetName(rootRow, idx);
        const childRecords = await readCustomBoqTable(childSheetTitle);
        if (!childRecords.length) continue;

        await replaceProductTableRows(token, childTableId, childRecords);
      }

      const { totalsMap } = await loadRootRowsAndTotals(rootTableId);

      // Auto-create history snapshot
      try {
        // Use the numbers the TOTAL sheet shows (child-sheet overhead included),
        // not the server's raw resource sums, so the Dashboard matches Excel.
        const summary = await readBoqSummary(rootSheetName || `${baseSheetName}0`);
        const chapters = (savedRootRows || []).map((row, idx) => {
          const t = totalsMap[row.id] || {};
          const x = rowsFromExcel[idx];
          return {
            display_id: row.Display_ID || String(idx + 1),
            name: row.Material_Name_GEO || "",
            material: x ? x.materialCost : t.material || 0,
            labour: x ? x.labourCost : t.labour || 0,
            equipment: x ? x.equipmentCost : t.equipment || 0,
            total: x ? x.totalPrice : t.total || 0,
          };
        });
        const isChapter = (c) => !String(c.display_id).replace(/\.0+$/, "").includes(".");
        const topLevel = chapters.filter(isChapter);
        const totalMaterial = summary?.material ?? topLevel.reduce((s, c) => s + c.material, 0);
        const totalLabour = summary?.labour ?? topLevel.reduce((s, c) => s + c.labour, 0);
        const totalEquipment = summary?.equipment ?? topLevel.reduce((s, c) => s + c.equipment, 0);
        // Grand total incl. unforeseen costs and VAT = the last "ჯამი" row of the sheet.
        const totalCost = summary?.grand ?? topLevel.reduce((s, c) => s + c.total, 0);
        await createBoqSnapshot(token, project.id, {
          version_id: rootTableId,
          version_name: selectedVersion?.version_name || null,
          total_material: totalMaterial,
          total_labour: totalLabour,
          total_equipment: totalEquipment,
          total_cost: totalCost,
          label: saveNote.trim() || null,
          chapters,
        });
        setSaveNote("");
      } catch (_) {
        // snapshot failure is non-critical
      }

      setExcelDirty(false);
      await loadLastSnapshot();
      await acknowledgeLatestSnapshot();
      setStatusMessage("BOQ saved to server from Excel.");
    } catch (error) {
      console.error("Save BOQ to server failed", error);
      setErrorMessage(error?.message || "Unable to save BOQ to server.");
    } finally {
      setBusy(false);
    }
  };

  const handleClearAllRows = async () => {
    if (!rootTableId || !token) return;

    const ok = await confirmDialog(
      "Delete ALL rows of this BOQ version from the server and clear the TOTAL sheet? This cannot be undone."
    );
    if (!ok) return;

    setBusy(true);
    setStatusMessage("");
    setErrorMessage("");
    try {
      await replaceProductTableRows(token, rootTableId, []);
      await loadRootRowsAndTotals(rootTableId);

      const emptyData = Array(10).fill(null).map(() => ({
        description: "",
        materialCost: 0,
        labourCost: 0,
        equipmentCost: 0,
        totalPrice: 0,
        percentage: "",
      }));

      await writeBoqTable(
        buildExcelProjectData(),
        emptyData,
        {},
        rootSheetName || `${baseSheetName}0`
      );

      setExcelDirty(false);
      setStatusMessage("All BOQ rows cleared from server and Excel.");
    } catch (error) {
      console.error("Clear rows error:", error);
      setErrorMessage(`Error: ${error?.message || "Failed to clear rows"}`);
    } finally {
      setBusy(false);
    }
  };

  const handleRefreshTotalsFromChildSheets = async () => {
    if (!rootTableId || !token) return;

    setBusy(true);
    setStatusMessage("");
    setErrorMessage("");

    try {
      const { mappedRows } = await loadRootRowsAndTotals(rootTableId);
      const childTotalsForExcel = {};
      const childSheetRefs = {};

      for (let idx = 0; idx < mappedRows.length; idx += 1) {
        const row = mappedRows[idx];
        const sheetName = buildChildSheetName(row, idx);
        const exists = await worksheetExists(sheetName);
        if (!exists) continue;

        const totals = await readChildSheetTotal(sheetName);
        childTotalsForExcel[idx] = totals;

        // Find summary row for formula references
        const summaryRow = await findChildSheetSummaryRow(sheetName);
        if (summaryRow > 0) {
          childSheetRefs[idx] = { sheetName, summaryRow };
        }

        mappedRows[idx] = {
          ...mappedRows[idx],
          materialCost: totals.material || 0,
          labourCost: totals.labour || 0,
          equipmentCost: totals.equipment || 0,
          totalPrice: totals.total || 0,
        };
      }

      await runExcelWithToast({
        action: () =>
          writeBoqTable(
            buildExcelProjectData(),
            mappedRows,
            childTotalsForExcel,
            rootSheetName || `${baseSheetName}0`,
            childSheetRefs
          ),
        successMessage: "Parent totals refreshed from child sheets",
      });

      await checkExcelSyncStatus();
      setStatusMessage("Parent totals updated from child sheets.");
    } catch (error) {
      console.error("Refresh totals failed", error);
      setErrorMessage(error?.message || "Unable to refresh totals from child sheets.");
    } finally {
      setBusy(false);
    }
  };

  // Add a chapter ("parent") or a sub-row ("child") to the TOTAL sheet. Numbers
  // are assigned automatically; then Save + child sheet generation run so the
  // new row is stored and gets its detail sheet.
  const handleAddRow = async (kind) => {
    const name = newRowName.trim();
    if (!name) {
      setErrorMessage("Enter a name for the new row first.");
      return;
    }
    setErrorMessage("");
    setStatusMessage("");
    try {
      const sheetName = rootSheetName || `${baseSheetName}0`;
      const selectedSheetRow = kind === "child" ? await getSelectedBoqSheetRow(sheetName) : null;
      const rows = await readBoqTable(sheetName);
      const nextRows = insertBoqRow(rows, { kind, name, selectedSheetRow });

      const childSheetRefs = {};
      nextRows.forEach((r, i) => {
        if (r.childRef) childSheetRefs[i] = r.childRef;
      });
      await runExcelWithToast({
        action: () =>
          writeBoqTable(buildExcelProjectData(), nextRows, {}, sheetName, childSheetRefs),
        successMessage: kind === "parent" ? "Chapter added" : "Sub-row added",
      });
      setNewRowName("");
      await handleGenerateChildSheetsFromExcel();
    } catch (error) {
      console.error("Add row failed", error);
      setErrorMessage(error?.message || "Unable to add row.");
    }
  };

  // Jump to the next TOTAL-sheet row whose number or name contains the text.
  const handleFindRow = async () => {
    const q = findText.trim().toLowerCase();
    if (!q) return;
    setErrorMessage("");
    try {
      const sheetName = rootSheetName || `${baseSheetName}0`;
      const rows = await readBoqTable(sheetName);
      const hits = rows.filter((r) => `${r.number} ${r.description}`.toLowerCase().includes(q));
      if (!hits.length) {
        setStatusMessage("");
        setErrorMessage("ასეთი სტრიქონი ვერ მოიძებნა.");
        return;
      }
      const at = findCursorRef.current % hits.length;
      findCursorRef.current += 1;
      const hit = hits[at];
      await Excel.run(async (context) => {
        const sheet = context.workbook.worksheets.getItem(getSafeSheetName(sheetName));
        sheet.activate();
        sheet.getRange(`C${hit.sheetRow}`).select();
        await context.sync();
      });
      setStatusMessage(`${at + 1}/${hits.length}: ${hit.number} ${hit.description}`);
    } catch (error) {
      setErrorMessage(error?.message || "ძებნა ვერ მოხერხდა.");
    }
  };

  const handleGenerateChildSheetsFromExcel = async () => {
    if (!rootTableId || !token) return;

    setBusy(true);
    setStatusMessage("");
    setErrorMessage("");

    try {
      const rowsFromExcel = await readBoqTable(rootSheetName || `${baseSheetName}0`);
      if (!rowsFromExcel.length) {
        throw new Error("No BOQ works found in Excel. Fill root sheet rows first.");
      }

      const records = buildRootRecords(rowsFromExcel, boqRows);
      await replaceProductTableRows(token, rootTableId, records);

      const { mappedRows } = await loadRootRowsAndTotals(rootTableId);
      const rowsWithData = mappedRows.filter((row) => row.row_id);

      if (!rowsWithData.length) {
        throw new Error("No saved rows found. Fill the root sheet and click Save BOQ first.");
      }

      let generatedCount = 0;
      const hierarchy = computeBoqHierarchy(mappedRows.map((r) => r.number));
      for (let idx = 0; idx < mappedRows.length; idx += 1) {
        const row = mappedRows[idx];
        if (!row.row_id || !(row.description || "").trim()) continue;
        if (hierarchy[idx].lastDescendant > idx) continue; // parent: sums its children

        const childSheetTitle = buildChildSheetName(row, idx);
        const childTable = await createOrGetChildBoqTable(token, rootTableId, row.row_id, baseSheetName);
        const exists = await worksheetExists(childSheetTitle);

        if (!exists) {
          const persistedChildRows = await fetchProductTableRows(token, childTable.id);
          const childSheetRows = mapServerChildRows(persistedChildRows);

          await writeCustomBoqTable(
            { language: project?.language || "geo", costConfig: project?.cost_config || null },
            childSheetRows,
            childSheetTitle,
            childTable.sheet_level || 1,
            row.description || ""
          );
          generatedCount += 1;
        }
      }

      // Refresh totals from child sheets with formula references
      const { mappedRows: refreshedRows } = await loadRootRowsAndTotals(rootTableId);
      const childTotalsForExcel = {};
      const childSheetRefs = {};

      for (let idx = 0; idx < refreshedRows.length; idx += 1) {
        const candidateSheetName = buildChildSheetName(refreshedRows[idx], idx);
        const exists = await worksheetExists(candidateSheetName);
        if (!exists) continue;

        try {
          const totals = await readChildSheetTotal(candidateSheetName);
          childTotalsForExcel[idx] = totals;

          const summaryRow = await findChildSheetSummaryRow(candidateSheetName);
          if (summaryRow > 0) {
            childSheetRefs[idx] = { sheetName: candidateSheetName, summaryRow };
          }

          refreshedRows[idx] = {
            ...refreshedRows[idx],
            materialCost: totals.material || 0,
            labourCost: totals.labour || 0,
            equipmentCost: totals.equipment || 0,
            totalPrice: totals.total || 0,
          };
        } catch (error) {
          console.warn(`Unable to read child sheet total for ${candidateSheetName}`, error);
        }
      }

      await runExcelWithToast({
        action: () =>
          writeBoqTable(
            buildExcelProjectData(),
            refreshedRows,
            childTotalsForExcel,
            rootSheetName || `${baseSheetName}0`,
            childSheetRefs
          ),
        successMessage: "Child sheets generated from Excel works",
      });

      await checkExcelSyncStatus();
      setStatusMessage(`Generated ${generatedCount} child sheet(s) from Excel rows.`);
    } catch (error) {
      console.error("Generate child sheets failed", error);
      setErrorMessage(error?.message || "Unable to generate child sheets from Excel.");
    } finally {
      setBusy(false);
    }
  };

  const handleOpenMaterialLookup = async () => {
    // Determine the active child sheet to add materials to
    try {
      const activeSheet = await Excel.run(async (context) => {
        const sheet = context.workbook.worksheets.getActiveWorksheet();
        sheet.load("name");
        await context.sync();
        return sheet.name;
      });

      // Check if active sheet is a child sheet (not the root)
      const rootName = rootSheetName || `${baseSheetName}0`;
      if (activeSheet !== rootName) {
        setActiveChildSheet(activeSheet);
      } else {
        setActiveChildSheet(null);
      }
    } catch {
      setActiveChildSheet(null);
    }

    setShowLookupModal(true);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const modalStyle = {
    content: {
      maxWidth: 540,
      margin: "auto",
      maxHeight: "85vh",
      overflow: "auto",
      background: "#0f172a",
      color: "#e2e8f0",
      border: "1px solid rgba(148, 163, 184, 0.35)",
      borderRadius: 12,
    },
    overlay: {
      backgroundColor: "rgba(2, 6, 23, 0.7)",
      zIndex: 20,
    },
  };

  return (
    <div className="taskpane-root taskpane-root--projects taskpane-root--boq">
      {typeof onBack === "function" && (
        <button className="link" onClick={onBack}>
          ← Back to projects
        </button>
      )}

      {remoteChange && (
        <div
          role="alert"
          data-no-translate="true"
          className="remote-change-banner"
        >
          <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 4 }}>
            ⚠ {remoteChange.by} saved changes to this project
          </div>
          <div style={{ fontSize: 12, marginBottom: 8, opacity: 0.85 }}>
            {remoteChange.at ? new Date(remoteChange.at).toLocaleTimeString() : ""}
            {remoteChange.version ? ` · ${remoteChange.version}` : ""} — your sheet is out of date.
            Load to get the changes (saving now would overwrite them).
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button
              type="button"
              className="boq-action-btn"
              onClick={handleLoadBoqFromServer}
              disabled={busy || !rootTableId}
            >
              ⬇ Load now
            </button>
            <button
              type="button"
              className="boq-action-btn"
              onClick={() => {
                seenSnapshotIdRef.current = remoteChange.id;
                setRemoteChange(null);
              }}
            >
              Dismiss
            </button>
          </div>
        </div>
      )}

      {approval && (
        <div
          role="status"
          data-no-translate="true"
          style={{ margin: "8px 0", padding: "10px 12px", borderRadius: 12, background: "#14532d", color: "#fff", border: "1px solid #22c55e" }}
        >
          <div style={{ fontWeight: 700, fontSize: 13 }}>🔒 ვერსია დამტკიცებულია და ჩაკეტილია</div>
          <div style={{ fontSize: 12, opacity: 0.9, marginTop: 3 }}>
            {approval.approved_by_name || "—"} · {approval.approved_at ? new Date(approval.approved_at).toLocaleDateString("ka-GE") : ""}
            {approval.note ? ` · ${approval.note}` : ""}
            {" — "}შენახვა გათიშულია. ცვლილებისთვის შექმენი ახალი ვერსია ისტორიიდან{canManageVersions ? " ან გააუქმე დამტკიცება" : ""}.
          </div>
          {canManageVersions && (
            <button
              type="button"
              className="boq-action-btn"
              style={{ marginTop: 6 }}
              onClick={async () => {
                try { await revokeApproval(token, project.id, rootTableId); await refreshApproval(); }
                catch (e) { setErrorMessage(e?.message || "Unable to revoke the approval"); }
              }}
            >
              დამტკიცების გაუქმება
            </button>
          )}
        </div>
      )}

      {onlineOthers.length > 0 && (
        <div
          data-no-translate="true"
          style={{ margin: "6px 0", padding: "6px 10px", borderRadius: 10, fontSize: 12, background: "var(--surface-soft)", border: "1px solid var(--surface-border)" }}
        >
          👥 ახლა ამ პროექტზე მუშაობს:{" "}
          {onlineOthers.map((o, i) => (
            <span key={o.user_id}>
              {i > 0 && ", "}
              <span
                style={{
                  display: "inline-block", width: 8, height: 8, borderRadius: "50%", marginRight: 4,
                  background: colorFor(o.user_id),
                }}
              />
              <strong>{o.name || "—"}</strong>
              {o.focus?.label ? ` (მწკრივი ${o.focus.label})` : ""}
            </span>
          ))}
        </div>
      )}

      <h2>Create BOQ (Bill of Quantities)</h2>

      {statusMessage && <div className="msg-success">{statusMessage}</div>}
      {errorMessage && <div className="msg-error">{errorMessage}</div>}

      <div className="form-section">
        <div className="field-group">
          <label htmlFor="date">Date</label>
          <input
            id="date"
            type="date"
            name="date"
            value={formData.date}
            onChange={handleChange}
            disabled={busy}
          />
        </div>

        <div className="field-group">
          <label htmlFor="customer">Customer</label>
          <input
            id="customer"
            type="text"
            name="customer"
            value={customerDisplay || ""}
            readOnly
            disabled
            placeholder="Customer name / INN"
          />
        </div>

        <div className="field-group">
          <label htmlFor="description">Description</label>
          <textarea
            id="description"
            name="description"
            value={formData.description}
            onChange={handleChange}
            disabled={busy}
            placeholder="Project description"
            rows={3}
          />
        </div>

        <div className="field-group">
          <label htmlFor="quantity">Quantity (m²)</label>
          <input
            id="quantity"
            type="text"
            name="quantity"
            value={formData.quantity}
            onChange={handleChange}
            disabled={busy}
            placeholder="Total quantity"
          />
        </div>
      </div>

      {/* ═══ BOQ Version selector ═══ */}
      <div className="boq-versions">
        <div className="boq-versions-row">
          <div className="field-group boq-version-select">
            <label htmlFor="boq-version">BOQ Version</label>
            <select
              id="boq-version"
              value={selectedVersionId || ""}
              onChange={(e) => handleSwitchVersion(e.target.value)}
              disabled={busy || versions.length === 0}
            >
              {versions.map((v) => (
                <option key={v.id} value={v.id}>
                  {v.version_name || "main"}
                  {v.is_primary_version ? "  ★ primary" : ""}
                </option>
              ))}
            </select>
          </div>

          {canManageVersions && (
            <>
              <button
                type="button"
                className="boq-action-btn"
                onClick={handleSetPrimary}
                disabled={
                  busy ||
                  !selectedVersion ||
                  selectedVersion?.is_primary_version
                }
                title="Mark this version as the primary one"
              >
                Set as primary
              </button>
              <button
                type="button"
                className="boq-action-btn boq-action-btn-danger"
                onClick={handleDeleteVersion}
                disabled={
                  busy ||
                  !selectedVersion ||
                  selectedVersion?.is_primary_version ||
                  versions.length <= 1
                }
                title="Delete the selected version"
              >
                Delete version
              </button>
            </>
          )}
        </div>

        {canManageVersions && (
          <div className="boq-versions-row">
            <input
              type="text"
              className="boq-version-input"
              placeholder="New version name (e.g. v2, draft, client-review)"
              value={newVersionName}
              onChange={(e) => setNewVersionName(e.target.value)}
              disabled={busy}
            />
            <button
              type="button"
              className="boq-action-btn boq-action-btn-accent"
              onClick={handleCreateVersion}
              disabled={busy || !newVersionName.trim()}
            >
              + Create new version
            </button>
          </div>
        )}
      </div>

      {/* ═══ Sync status: last save + whether the open sheet still matches it ═══ */}
      <div className="boq-sync">
        <span className="boq-sync__saved">
          {lastSnapshot ? (
            <>
              🕓 Last saved {formatSnapshotDate(lastSnapshot.created_at)}
              {lastSnapshot.created_by_name ? ` · ${lastSnapshot.created_by_name}` : ""}
            </>
          ) : (
            "🕓 Never saved to server yet"
          )}
        </span>
        <span
          className={
            "boq-sync__dot " +
            (excelDirty === true ? "is-dirty" : excelDirty === false ? "is-clean" : "is-unknown")
          }
        >
          {excelDirty === true
            ? "● Excel has unsaved changes"
            : excelDirty === false
            ? "✓ Excel matches server"
            : "? Not checked yet"}
        </span>
        <button
          type="button"
          className="icon-btn boq-sync__check"
          onClick={checkExcelSyncStatus}
          disabled={checkingDirty || !rootTableId}
          title="Compare the open TOTAL sheet against the server"
        >
          {checkingDirty ? "…" : "🔄"}
        </button>
      </div>

      <div className="boq-panel">
        {!canEdit && userRole === "viewer" && (
          <div className="boq-readonly-banner">
            👁 View-only mode — you can load data but cannot modify it.
          </div>
        )}

        {/* ── Sync section ── */}
        <div className="boq-section-label">Sync with Server</div>
        <input
          type="text"
          placeholder="შენიშვნა შენახვაზე (რა შეცვალე?) — ჩანს ისტორიაში"
          value={saveNote}
          maxLength={120}
          onChange={(e) => setSaveNote(e.target.value)}
          disabled={busy || !canEdit}
          style={{ width: "100%", boxSizing: "border-box", padding: "6px 8px", borderRadius: 8, fontSize: 12, marginBottom: 8 }}
        />
        <div className="boq-tile-grid boq-tile-grid--2">
          <button
            type="button"
            className="boq-tile boq-tile--load"
            onClick={handleLoadBoqFromServer}
            disabled={busy || !rootTableId}
          >
            <span className="boq-tile__icon">⬇</span>
            <span className="boq-tile__name">Load</span>
            <span className="boq-tile__hint">Server → Excel</span>
          </button>
          <button
            type="button"
            className="boq-tile boq-tile--save"
            onClick={handleSaveBoqToServer}
            disabled={busy || !rootTableId || !canEdit || !!approval}
          >
            <span className="boq-tile__icon">⬆</span>
            <span className="boq-tile__name">Save</span>
            <span className="boq-tile__hint">Excel → Server</span>
          </button>
        </div>

        {/* ── Resource Lookup ── */}
        <button
          type="button"
          className="boq-wide-btn boq-wide-btn--lookup"
          onClick={handleOpenMaterialLookup}
          disabled={!canEdit}
        >
          🔍 Resource Lookup
          <span className="boq-wide-btn__hint">search catalog &amp; add to child sheet</span>
        </button>

        {/* ── Chapters & sub-rows ── */}
        <div className="boq-section-label">Chapters</div>
        <div style={{ display: "flex", gap: 6, marginBottom: 8 }}>
          <input
            type="search"
            placeholder="🔎 სტრიქონის ძებნა (ნომერი ან სახელი)"
            value={findText}
            onChange={(e) => { setFindText(e.target.value); findCursorRef.current = 0; }}
            onKeyDown={(e) => { if (e.key === "Enter") handleFindRow(); }}
            style={{ flex: 1, padding: "6px 8px", borderRadius: 8, fontSize: 12, boxSizing: "border-box" }}
          />
          <button type="button" className="boq-wide-btn" onClick={handleFindRow} disabled={!findText.trim()}>
            შემდეგი
          </button>
        </div>
        <input
          type="text"
          style={{ width: "100%", boxSizing: "border-box", padding: "6px 8px", marginBottom: 8 }}
          placeholder="Name of the new row"
          value={newRowName}
          onChange={(e) => setNewRowName(e.target.value)}
          disabled={busy || !canEdit}
        />
        <div className="boq-tile-grid boq-tile-grid--2">
          <button
            type="button"
            className="boq-tile"
            onClick={() => handleAddRow("parent")}
            disabled={busy || !rootTableId || !canEdit}
            title="Add a new chapter (1, 2, 3, ...) at the end of the TOTAL sheet"
          >
            <span className="boq-tile__icon">➕</span>
            <span className="boq-tile__name">Add Parent</span>
            <span className="boq-tile__hint">New chapter</span>
          </button>
          <button
            type="button"
            className="boq-tile"
            onClick={() => handleAddRow("child")}
            disabled={busy || !rootTableId || !canEdit}
            title="Click a cell in any row of the TOTAL sheet first, then add a child under it (2 -> 2.1 -> 2.1.1)"
          >
            <span className="boq-tile__icon">↳</span>
            <span className="boq-tile__name">Add Child</span>
            <span className="boq-tile__hint">Under selected row</span>
          </button>
        </div>

        <button
          type="button"
          className="boq-wide-btn"
          style={{ marginTop: 8 }}
          onClick={() => setShowComments(true)}
          disabled={!rootTableId}
          title="Notes on BOQ rows"
        >
          💬 კომენტარები{commentCount > 0 ? ` (${commentCount})` : ""}
          <span className="boq-wide-btn__hint">შენიშვნები სტრიქონებზე</span>
        </button>

        {/* ── Excel Sheets section ── */}
        <div className="boq-section-label">Excel Sheets</div>
        <div className="boq-tile-grid boq-tile-grid--3">
          <button
            type="button"
            className="boq-tile"
            onClick={handleCreateBOQ}
            disabled={busy || !formData.date || !canEdit}
            title="Re-render the TOTAL sheet from server data with full formatting"
          >
            <span className="boq-tile__icon">📋</span>
            <span className="boq-tile__name">Render</span>
            <span className="boq-tile__hint">Format TOTAL sheet</span>
          </button>
          <button
            type="button"
            className="boq-tile"
            onClick={handleGenerateChildSheetsFromExcel}
            disabled={busy || !rootTableId || !canEdit}
            title="Create child detail sheets for each chapter row in the TOTAL sheet"
          >
            <span className="boq-tile__icon">📑</span>
            <span className="boq-tile__name">Child Sheets</span>
            <span className="boq-tile__hint">Create detail sheets</span>
          </button>
          <button
            type="button"
            className="boq-tile"
            onClick={handleRefreshTotalsFromChildSheets}
            disabled={busy || !rootTableId || !canEdit}
            title="Pull totals from all child sheets into the TOTAL sheet"
          >
            <span className="boq-tile__icon">🔄</span>
            <span className="boq-tile__name">Refresh</span>
            <span className="boq-tile__hint">Update TOTAL</span>
          </button>
        </div>

        {/* ── Automation ── */}
        <div className="boq-section-label">ავტომატიზაცია</div>
        <button
          type="button"
          className="boq-wide-btn boq-wide-btn--lookup"
          onClick={() => { window.location.hash = "#tools"; }}
          title="Audit, purchase list and quantity scaling"
        >
          🧰 ხელსაწყოები
          <span className="boq-wide-btn__hint">შემოწმება · უწყისი · გადამრავლება</span>
        </button>
        <button
          type="button"
          className="boq-wide-btn boq-wide-btn--index"
          onClick={() => { window.location.hash = "#indexation"; }}
          disabled={!rootTableId}
          title="Increase or decrease prices by resource type across all child sheets"
        >
          📈 ინდექსირება
          <span className="boq-wide-btn__hint">ფასების ინდექსაცია</span>
        </button>

        {/* ── History ── */}
        <button
          type="button"
          className="boq-wide-btn boq-wide-btn--history"
          onClick={() => setShowHistory(true)}
          disabled={!rootTableId}
          title="View save history and compare snapshots"
        >
          🕓 Save History
          <span className="boq-wide-btn__hint">compare any two saves</span>
        </button>

        {/* ── Danger ── */}
        <button
          type="button"
          className="boq-action-btn boq-action-btn-danger boq-clear-btn"
          onClick={handleClearAllRows}
          disabled={busy || !rootTableId || !canEdit}
          title="Delete all BOQ rows from server and Excel"
        >
          🗑 Clear All Rows
        </button>
      </div>

      <BoqCommentsPanel
        isOpen={showComments}
        onClose={() => setShowComments(false)}
        token={token}
        projectId={project?.id}
        versionId={rootTableId}
        sheetName={rootSheetName || `${baseSheetName}0`}
        canEdit={canEdit}
        canModerate={canManageVersions}
        myUserId={myUserId}
        onChanged={setCommentCount}
      />

      {showHistory && (
        <BoqHistoryPanel
          token={token}
          projectId={project?.id}
          onClose={() => setShowHistory(false)}
          canRestore={canManageVersions}
          onApproved={refreshApproval}
          onBranched={(version) => loadVersionsAndSelect(version?.id)}
          onRestored={() => {
            setShowHistory(false);
            performLoadBoqFromServer();
          }}
        />
      )}

      {/* Sheet-driven material lookup modal */}
      <Modal
        isOpen={showLookupModal}
        onRequestClose={() => setShowLookupModal(false)}
        contentLabel="Resource Lookup"
        style={modalStyle}
        ariaHideApp={false}
      >
        <div className="boq-modal-header">
          <h3 style={{ margin: 0 }}>რესურსების ძიება / Resource Lookup</h3>
          <button className="boq-action-btn" onClick={() => setShowLookupModal(false)}>Close</button>
        </div>
        {activeChildSheet && (
          <div className="boq-modal-target">
            Adding to: <strong>{activeChildSheet}</strong>
          </div>
        )}
        <SheetMaterialLookupPanel token={token} targetSheet={activeChildSheet} />
      </Modal>

      {/* Load BOQ confirmation modal */}
      <Modal
        isOpen={showLoadConfirm}
        onRequestClose={() => setShowLoadConfirm(false)}
        contentLabel="Confirm load"
        style={{
          content: { ...modalStyle.content, maxWidth: 460, maxHeight: "auto" },
          overlay: modalStyle.overlay,
        }}
        ariaHideApp={false}
      >
        <div className="boq-modal-header">
          <h3 style={{ margin: 0 }}>Load BOQ?</h3>
        </div>
        <p style={{ margin: "8px 0 14px", fontSize: 13, lineHeight: 1.5 }}>
          Loading version <strong>"{selectedVersion?.version_name || "—"}"</strong> will
          <strong> delete all existing BOQ sheets</strong> (root and child sheets) from this workbook and
          replace them with the data from the server.
          <br />
          <br />
          Do you want to continue?
        </p>
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <button className="boq-action-btn" onClick={() => setShowLoadConfirm(false)}>
            Cancel
          </button>
          <button className="primary" onClick={performLoadBoqFromServer} disabled={busy}>
            Yes, load and replace
          </button>
        </div>
      </Modal>
    </div>
  );
}
